<template>
  <PageLayoutWithFilter>
    <template slot="header">
      <div class="d-flex m-3 mx-lg-0">
        <Breadcrumb :list="breadcrumbList" />
        <ExportData
          id="exportData"
          class="ml-sm-2 mt-2 mt-sm-0"
          :canExport="$can('use', 'report.export')"
          :hasPptx="true"
          :hasXlsx="true"
          @export="handleExportClick"
        />
        <GroupsTreeSelect class="ml-md-1" @selectItem="selectGroupItem" />
      </div>
    </template>
    <template slot="main">
      <div class="page-reports">
        <b-card :class="{ 'inactive-group-card': inactive }">
          <header slot="header">
            <b-row>
              <b-col :title="sectionTitles.overall" md="10">
                {{ reportsResources.overall.header }}
                <span v-if="groupName">({{ groupName }})</span>
                <GroupsInactive v-if="inactive" />
              </b-col>
              <b-col v-if="!isEmptyState" md="2">
                <tooltip-info
                  :contentClasses="'float-right'"
                  :message="reportsResources.tooltips.overall"
                  type="ask"
                ></tooltip-info>
              </b-col>
            </b-row>
          </header>
          <div v-if="!isEmptyState">
            <div class="flex over-score">
              <div class="flex flex-center report-date">
                <Overall :score="score" />
                <small v-if="score" class="mt-2">
                  {{
                    reportsResources.overall.reportDate
                      | parse({ DATE: $moment(date).format('DD/MM/YYYY') })
                  }}
                </small>
                <small class="engagement-notice" v-if="score">
                  {{ reportsResources.overall.reportNotice }}
                </small>
              </div>
              <HistoricChart :history="history" />
            </div>
            <div class="text-center" v-if="!inactive">
              <OpenActionPlanButton
                :groupId="groupId"
                @click="openActionPlanModal"
              />
            </div>
          </div>
          <div class="flex over-score" v-else>
            <div class="empty-state-box">
              <video
                src="home/empty-state-metrics.mov"
                autoplay
                loop
                playsinline
              ></video>
              <label id="doesnt-exist-tickets-box">
                {{ reportsResources.emptyState.noMetricsMessage | parse }}
              </label>
              <b-button
                v-if="$can('see', 'inviteUsers')"
                @click="handleEmptyButton"
                variant="tertiary"
                size="sm"
              >
                <span class="d-lg-inline-block">
                  {{ reportsResources.emptyState.inviteUser }}
                </span>
              </b-button>
            </div>
          </div>
        </b-card>
        <br /><br />

        <b-card
          :class="[
            'metrics-container',
            { 'chart-layout': isChart },
            { 'inactive-group-card': inactive },
          ]"
          v-if="!isEmptyState"
        >
          <header :title="sectionTitles.metrics" slot="header">
            <div class="d-flex">
              {{ reportsResources.metricList.header }}
              <div class="switch-metric-type">
                <b-link
                  @click.prevent="switchMetricType('chart')"
                  v-show="metricType === 'list'"
                >
                  <i class="mdi mdi-chart-line-variant" aria-hidden="true"></i>
                  {{ reportsResources.metricList.showChart }}
                </b-link>
                <b-link
                  @click.prevent="switchMetricType('list')"
                  v-show="metricType === 'chart'"
                >
                  <i class="mdi mdi-menu" aria-hidden="true"></i>
                  {{ reportsResources.metricList.showList }}
                </b-link>
              </div>
            </div>
          </header>
          <MetricList
            :metricType="metricType"
            :list="metrics"
            :canViewMetricsInfo="$can('use', 'report.export')"
          >
          </MetricList>
        </b-card>
        <br /><br />

        <!-- smart metrics -->
        <b-card
          v-if="showSmartMetrics"
          :class="{ 'inactive-group-card': inactive }"
        >
          <header slot="header">
            <b-row>
              <b-col :title="reportsResources.smartMetrics.title" md="12">
                {{ reportsResources.smartMetrics.title }}
                <p class="smart-metrics-description">
                  {{ reportsResources.smartMetrics.description }}
                </p>
              </b-col>
            </b-row>
          </header>
          <SmartMetricList
            metricType="list"
            :list="smartMetrics"
            :canViewMetricsInfo="$can('use', 'report.export')"
            :isManager="isManager"
          >
          </SmartMetricList>
        </b-card>
        <br v-if="showSmartMetrics" />
        <br v-if="showSmartMetrics" />

        <!-- ENPS -->
        <b-card
          v-if="!isEmptyState"
          :class="{ 'inactive-group-card': inactive }"
        >
          <header slot="header">
            <b-row>
              <b-col :title="sectionTitles.enps" cols="10" md="11">
                {{ reportsResources.enpsTitle }}
              </b-col>
              <b-col cols="2" md="1">
                <tooltip-info
                  :contentClasses="'float-right'"
                  :message="reportsResources.tooltips.enps"
                  type="ask"
                ></tooltip-info>
              </b-col>
            </b-row>
          </header>
          <div v-if="enps.historic" class="nps-component pt-4">
            <Enps :enps="enps" :label="getLabelByScore(enps)" />
            <div class="history">
              <EnpsChart :enps="enps.historic" :isLeadership="false" />
            </div>
          </div>
          <div v-else class="enps-empty">
            <h3>{{ reportsResources.metric.enps.empty }}</h3>
          </div>
        </b-card>
        <br /><br />

        <!-- LNPS -->
        <b-card
          v-if="!isEmptyState"
          :class="{ 'inactive-group-card': inactive }"
        >
          <header slot="header">
            <b-row>
              <b-col :title="sectionTitles.lnps" cols="10" md="11">
                {{ reportsResources.lnpsTitle }}
              </b-col>
              <b-col cols="2" md="1">
                <tooltip-info
                  :contentClasses="'float-right'"
                  :message="reportsResources.tooltips.enps"
                  type="ask"
                ></tooltip-info>
              </b-col>
            </b-row>
          </header>
          <div v-if="lnps.historic" class="nps-component pt-4">
            <Enps :enps="lnps" :label="getLabelByScore(lnps)" />
            <div class="history">
              <EnpsChart :enps="lnps.historic" :isLeadership="true" />
            </div>
          </div>
          <div v-else class="enps-empty">
            <h3>{{ reportsResources.metric.lnps.empty }}</h3>
          </div>
        </b-card>
        <br />
        <br />

        <b-card
          :class="{
            'recommendation-container': true,
            'inactive-group-card': inactive,
          }"
          v-if="
            $can('use', 'recommendation') &&
              recommendations$ &&
              recommendations$.length &&
              !isEmptyState &&
              !inactive
          "
        >
          <header slot="header">
            <b-row>
              <b-col :title="sectionTitles.recomendations" cols="10" md="11">
                {{ reportsResources.recommendation.header }}
              </b-col>
              <b-col v-if="score" cols="2" md="1">
                <tooltip-info
                  :contentClasses="'float-right'"
                  :message="reportsResources.tooltips.recommendation"
                  type="ask"
                ></tooltip-info>
              </b-col>
            </b-row>
          </header>
          <RecommendationCarousel
            v-if="recommendations$ && recommendations$.length"
            :recommendations="recommendations$"
            @recommendationButtonClicked="recommendationButtonClicked"
          />
        </b-card>
        <!-- END - Card-Recomendation -->

        <modal-plans
          ref="component-modal-plans"
          :title="reportsResources.modalPlans.title"
          @hide="handleHide"
        >
        </modal-plans>

        <b-modal
          id="modal-unauthorized"
          :title="reportsResources.unauthorizedModal.title"
          cancel-variant="outline-primary"
          ref="modal-unauthorized"
          header-bg-variant="danger"
        >
          <p class="txt-20">{{ reportsResources.unauthorizedModal.message }}</p>
          <template v-slot:modal-footer>
            &nbsp;
          </template>
        </b-modal>
      </div>
      <new-action-plan />
    </template>
    <template slot="filter-collapsed">
      <user-attributes-filter
        :collapsed="true"
        :groupOid="groupOid"
        @loadUserAttributeReport="loadUserAttributeReport"
      />
    </template>
    <template slot="filter">
      <user-attributes-filter
        :collapsed="false"
        :groupOid="groupOid"
        @loadUserAttributeReport="loadUserAttributeReport"
      />
    </template>
  </PageLayoutWithFilter>
</template>

<script>
import { getUser, isManager } from '@/service/user'
import { mapGetters } from 'vuex'
import ability from '@/helpers/ability'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import GroupsTreeSelect from '@/components/Groups/GroupsTreeSelect.vue'
import NewActionPlan from '@/components/ActionPlan/New.js'
import OpenActionPlanButton from '@/components/ActionPlan/OpenActionPlanButton.vue'
import { ACTION_PLAN_EVENT_OPEN, TEMPLATES } from '@/components/ActionPlan/Enum'
import Enps from '@/components/Reports/Enps.vue'
import EnpsChart from '@/components/Reports/EnpsChart.vue'
import HistoricChart from '@/components/Reports/HistoricChart.vue'
import MetricList from '@/components/Reports/MetricList.vue'
import ModalPlans from '@/components/Modal/ModalPlans.vue'
import Overall from '@/components/Charts/Overall.vue'
import PageLayoutWithFilter from '@/components/Home/PageLayoutWithFilter.vue'
import TooltipInfo from '@/components/TooltipInfo.vue'
import RecommendationCarousel from '@/components/RecommendationCarousel.vue'
import { getRecommendations } from '@/service/recommendation'
import { from } from 'rxjs'
import { switchMap, pluck, filter } from 'rxjs/operators'
import { APP_MODAL_OPEN, RECOMMENDATION } from '@/components/Modal/Enum'
import ExportData from '@/components/Reports/ExportData.vue'
import GroupsInactive from '@/components/Groups/GroupsInactive.vue'
import UserAttributesFilter from '@/components/Reports/Filters/UserAttributesFilter.vue'
import SmartMetricList from '@/components/Reports/SmartMetricList.vue'

export default {
  components: {
    Breadcrumb,
    GroupsTreeSelect,
    HistoricChart,
    Enps,
    EnpsChart,
    MetricList,
    ModalPlans,
    Overall,
    PageLayoutWithFilter,
    TooltipInfo,
    RecommendationCarousel,
    NewActionPlan,
    OpenActionPlanButton,
    ExportData,
    GroupsInactive,
    UserAttributesFilter,
    SmartMetricList,
  },
  data() {
    return {
      metricType: 'list',
    }
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
      selectedGroup: 'user/selectedGroup',
      selectedReport: 'userAttributes/selectedReport',
    }),
    ...mapGetters('reports', {
      groupName: 'groupName',
      metrics: 'metrics',
      smartMetrics: 'smartMetrics',
      history: 'history',
      score: 'score',
      enps: 'enps',
      lnps: 'lnps',
      lowMetrics: 'lowMetrics',
      isEmptyState: 'isEmptyState',
      date: 'date',
      inactive: 'inactive',
    }),

    sectionTitles: function() {
      return this.reportsResources.titles
    },

    isChart: function() {
      return this.metricType === 'chart'
    },

    reportsResources: function() {
      let reportsResources = this.resources.page.reports.engagement

      const mapped = {
        enpsTitle: reportsResources.metric.enps.title,
        lnpsTitle: reportsResources.metric.lnps.title,
        filters: reportsResources.filters,
        emptyState: reportsResources.emptyState,
      }

      reportsResources = { ...reportsResources, ...mapped }

      return reportsResources
    },
    groupId: function() {
      return this.$route.params.groupId
    },
    groupOid() {
      return this.groupId === 'company' ? this.user.companyId : this.groupId
    },
    user() {
      return getUser()
    },
    breadcrumbList() {
      return [
        {
          label: this.resources.general.menu.report,
        },
        {
          label: this.resources.general.menu.engagement,
        },
      ]
    },
    isManager() {
      return isManager()
    },
    showSmartMetrics() {
      const smartMetrics = this.smartMetrics
      return (
        !this.isEmptyState &&
        smartMetrics &&
        smartMetrics.length > 0 &&
        smartMetrics.filter(s => s.score !== null).length > 0
      )
    },
  },
  methods: {
    loadUserAttributeReport(report) {
      this.$store.dispatch('reports/reload', { _id: report.id })
    },
    handleExportClick(type) {
      this.$can('use', 'report.export')
        ? this.exportFile(type)
        : this.$refs['component-modal-plans'].show()
    },
    exportFile(type) {
      // eslint-disable-next-line no-undef
      const env = window.env || process.env
      const groupId = this.selectedReport
        ? this.selectedReport.id
        : this.groupOid

      const url = `${env.VUE_APP_EXPORTER_API_HOST}/engagement/${groupId}/${type}?token=${localStorage.jwt}&language=${localStorage.languageabrev}`
      window.open(url, '_blank')
    },
    recommendationButtonClicked(recommendation) {
      this.$root.$emit(APP_MODAL_OPEN, { modal: RECOMMENDATION })
      this.$store.dispatch('recommendation/initializePost', {
        title: recommendation.title,
        image: recommendation.image,
        metrics: recommendation.metrics,
      })
      this.$store.dispatch(
        'recommendation/getRecommendationById',
        recommendation.id
      )
    },
    switchMetricType(type) {
      this.metricType = type
    },
    selectGroupItem(group) {
      const groupId = group._id
      if (groupId !== 'company' && !group.enable) {
        this.$refs['component-modal-plans'].show()
        return false
      }

      this.$root.$emit('app:message:hide')
      this.$store.commit('reports/hideMessage')

      this.$router.push({ name: 'reports', params: { groupId } })
      if (!this.isManager) {
        this.$store.dispatch('userAttributes/loadUserAttributesReports', {
          groupId: this.groupOid,
          unselect: true,
        })
      }
      this.$store.dispatch('reports/reload', group).catch(error => {
        const { status } = error.response
        if (status === 401) {
          this.$refs['modal-unauthorized'].show()
        }
      })
    },
    handleEmptyButton() {
      this.$root.$emit('track', {
        event: 'Clicou em convidar usuário (Empty Stage)',
        data: {
          label: 'Engajamento',
        },
      })
      this.$router.push({ name: 'dashboard.users.send.invite' })
    },
    openActionPlanModal() {
      this.$root.$emit(ACTION_PLAN_EVENT_OPEN, TEMPLATES.LIST_ACTION_PLANS)
    },
    getLabelByScore(nps) {
      const labels = this.resources.page.reports.engagement.metric.enps
      const score = nps && nps.score ? nps.score : 0
      switch (true) {
        case score <= 0:
          return labels.low
        case score <= 50:
          return labels.okay
        case score <= 75:
          return labels.good
        case score <= 100:
          return labels.veryGood
        default:
          return labels.low
      }
    },
    handleHide() {
      this.$store.dispatch('user/updateSelectedGroup', undefined)
      this.$router.push({ name: 'reports', params: { groupId: 'company' } })
      this.$store.dispatch('reports/reload', { _id: 'company' })
    },
  },
  created() {
    const selectedReport = this.selectedReport || false
    const isSelected =
      selectedReport && selectedReport.key.split('|')[0] === this.groupOid

    if (isSelected) {
      return this.loadUserAttributeReport(selectedReport)
    }

    const groupId = this.groupId
    const selectedGroup = this.selectedGroup
    if (selectedGroup && selectedGroup !== groupId) {
      this.$router.push({ name: 'reports', params: { groupId: selectedGroup } })
      return this.$store
        .dispatch('reports/reload', {
          _id: selectedGroup,
        })
        .catch(error => {
          const { status } = error.response
          if (status === 401) {
            this.$refs['modal-unauthorized'].show()
          }
        })
    }

    this.$store.dispatch('reports/getEnps', groupId)
    this.$store.dispatch('reports/getLnps', groupId)
    this.$store.dispatch('reports/getReportsByGroup', groupId).catch(error => {
      const { status } = error.response
      if (status === 401) {
        this.$refs['modal-unauthorized'].show()
      }
    })
  },
  mounted: function() {
    this.$ability.update(ability().rules)
    this.$root.$emit('setPageTitle', { context: this, path: 'reports' })
  },
  subscriptions() {
    const recommendations$ = this.$watchAsObservable('lowMetrics').pipe(
      pluck('newValue'),
      filter(metrics => metrics && metrics.length),
      switchMap(metrics => from(getRecommendations(metrics)))
    )

    return {
      recommendations$,
    }
  },
  beforeDestroy() {
    this.$store.commit('reports/reset')
    this.$store.commit('reports/hideMessage')
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

header {
  .d-flex {
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }
}

.over-score {
  @media (max-width: 767px) {
    flex-direction: column;
  }
}

button {
  .material-icons {
    vertical-align: baseline;
  }
  span {
    vertical-align: text-bottom;
  }
}

.metrics-container .subTitle {
  color: map-get($theme-colors, 'gray02');
  font-size: 12px;
  text-decoration: none;
  margin: 0;
}

.empty-state-box {
  align-items: center;
  padding: 15px;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  text-align: center;
  width: 100%;

  video {
    text-align: center;
    flex-grow: 1;
    width: 75%;
  }

  button {
    margin: 15px 0;
  }

  label {
    cursor: default;
    color: map-get($theme-colors, 'gray03');
    font-size: 21px;
    line-height: 21px;
    margin-top: 10px;
    padding: 0 94px;
    max-width: 690px;
  }
}

.page-reports {
  margin-bottom: 40px;
}

.nav.header .nav-link {
  margin: 0rem !important;
}

.chart-layout {
  .card-body {
    padding: 0px;
    margin: 0px;
  }
}

.card {
  .card-header {
    font-size: 24px;

    .smart-metrics-description {
      font-size: 10pt;
      margin-bottom: 0px;
      font-style: italic;
    }
  }

  .card-body {
    @media (max-width: 1300px) {
      margin: 0px;
      padding: 0px 0px 10px 0px;
    }
    @media (max-width: 990px) {
      padding: 1.25rem;
    }
    @media (max-width: 576px) {
      padding: 0px 0px 10px 0px;
    }
  }

  .flex {
    display: flex;
    justify-content: space-around;
    align-items: center;
  }
}

.switch-metric-type {
  font-size: 13px;
}

.nps-component {
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  @media (max-width: 767px) {
    flex-direction: column;
  }

  .history {
    flex-grow: 1;
    padding: 0 20px;

    canvas {
      max-height: 300px !important;
    }
  }
}

.recommendation-container > .card-body {
  padding: 20px;
}

.report-date {
  flex-direction: column;
}

.inactive-group-card {
  background: map-get($theme-colors, 'gray08') !important;
  .card-header {
    background: map-get($theme-colors, 'gray08') !important;
  }
}

.engagement-notice {
  padding: 15px;
  max-width: 315px;
  text-align: center;
  font-style: italic;
}

.enps-empty {
  color: orange;
}
</style>
